import * as React from "react";
import * as ReactQuery from "react-query";
import axios from "axios";

import { InitialStateMinimumRequirements_GraphQl } from "../../../Common";

import { DataSource_LegacyCheckoutConfig_QueryKey } from "./query-key-factory";

export interface DataSourceLegacyCheckoutConfigConsumerInterface {
  data: DataSourceLegacyCheckoutConfig_Result["data"];
  status: DataSourceLegacyCheckoutConfig_Result["status"];
}
const defaultValue =
  {} as unknown as DataSourceLegacyCheckoutConfigConsumerInterface;
const DataSourceLegacyCheckoutConfigContext = React.createContext(defaultValue);

export interface DataSourceLegacyCheckoutConfig_ControllerInterface {
  mutate: DataSourceLegacyCheckoutConfig_Result["mutate"];
  mutateAsync: DataSourceLegacyCheckoutConfig_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataSourceLegacyCheckoutConfig_ControllerInterface;
const DataSourceLegacyCheckoutConfig_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataSourceLegacyCheckoutConfig_Request {}
export interface DataSourceLegacyCheckoutConfig_Response {
  data: any;
  error?: unknown;
  errors?: unknown[];
}
export type DataSourceLegacyCheckoutConfig_Result =
  ReactQuery.UseMutationResult<
    DataSourceLegacyCheckoutConfig_Response,
    unknown,
    DataSourceLegacyCheckoutConfig_Request,
    unknown
  >;

export interface DataSourceLegacyCheckoutConfigProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataSourceLegacyCheckoutConfigProvider: React.FunctionComponent<
  DataSourceLegacyCheckoutConfigProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataSource_LegacyCheckoutConfig_QueryKey.legacyCheckoutConfig(props);
  }, [props]);

  const mutationFn: ReactQuery.MutationFunction<
    DataSourceLegacyCheckoutConfig_Response,
    DataSourceLegacyCheckoutConfig_Request
  > = React.useCallback(
    async (request) => {
      const headers = {
        "Content-Type": "application/json",
      };

      const { data } = await axios.get<DataSourceLegacyCheckoutConfig_Response>(
        `/checkout/cart/CheckoutConfig`,
        { headers }
      );

      if (data.error || data.errors) {
        const firstErrorMessage =
          (!!data.error ? (data.error as string) : "") || "Unknown Error";

        throw new Error(firstErrorMessage);
      }

      return data;
    },
    [initialState]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn);

  const value: DataSourceLegacyCheckoutConfigConsumerInterface = React.useMemo(
    () => ({
      data: mutation.data,
      status: mutation.status,
    }),
    [mutation.data, mutation.status]
  );
  const controllerValue: DataSourceLegacyCheckoutConfig_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataSourceLegacyCheckoutConfigContext.Provider value={value}>
      <DataSourceLegacyCheckoutConfig_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataSourceLegacyCheckoutConfig_ControllerContext.Provider>
    </DataSourceLegacyCheckoutConfigContext.Provider>
  );
};

export const useDataSourceLegacyCheckoutConfig = () => {
  const context = React.useContext(DataSourceLegacyCheckoutConfigContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataSourceLegacyCheckoutConfig must be used within a DataSourceLegacyCheckoutConfigProvider"
    );
  }
  return context;
};

export const useDataSourceLegacyCheckoutConfig_asController = () => {
  const context = React.useContext(
    DataSourceLegacyCheckoutConfig_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataSourceLegacyCheckoutConfig_asController must be used within a DataSourceLegacyCheckoutConfigProvider"
    );
  }
  return context;
};
